/**
 * src/data/BLUFBank.js
 * Hardcoded scenario bank for Part 3: Email Like A Pro (The BLUF Method)
 */

export const blufBank = [
  {
    "id": "bluf_01",
    "scenarioContext": "You need the Q3 marketing budget approved by Friday EOD to lock in early-bird ad rates.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Needed: Approve Q3 Marketing Budget (Due Friday 5 PM)",
        "opener": "I am writing to request final approval for the $15,000 Q3 marketing spend.",
        "ask": "Please review the attached breakdown and approve by Friday at 5 PM to secure early-bird vendor rates.",
        "rationale": "Clear action tag, immediate value proposition, and explicit deadline."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Q3 Budget Review",
        "opener": "Hope you're having a great week! I wanted to check in about the Q3 marketing numbers.",
        "ask": "Let me know what you think when you get a chance so we can lock in rates.",
        "rationale": "Friendly, but buries the urgency and lacks a concrete deadline."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "URGENT READ NOW",
        "opener": "So about the money for next quarter...",
        "ask": "Call me ASAP if confused.",
        "rationale": "Unprofessional subject line, informal tone, and zero actionable context."
      }
    ]
  },
  {
    "id": "bluf_02",
    "scenarioContext": "You need VP approval to hire a specialized database contractor for a 4-week sprint, starting next Monday.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Approval Required: Database Contractor Contract (Start Date: Monday)",
        "opener": "I am requesting approval to hire a specialized database contractor for a 4-week, $10,000 engagement.",
        "ask": "Please sign the attached SOW by tomorrow at noon so they can begin onboarding on Monday.",
        "rationale": "Directly states the request, cost, and exact deadline required to hit the start date."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Contractor help needed",
        "opener": "The team is really underwater right now with the database migration.",
        "ask": "I found a great contractor, can you look at their SOW when you have time?",
        "rationale": "Starts with a complaint rather than the thesis, and leaves the approval timeline open-ended."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Quick question",
        "opener": "Are we allowed to hire freelancers right now?",
        "ask": "Let me know, it's pretty urgent.",
        "rationale": "Vague subject line, creates more questions than it answers, and lacks any specific details."
      }
    ]
  },
  {
    "id": "bluf_03",
    "scenarioContext": "You need the CFO to authorize an emergency $5k increase in AWS spend to handle a viral traffic spike.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Urgent Approval: $5k AWS Spend Increase for Traffic Spike",
        "opener": "Due to a viral PR hit, our servers are at 95% capacity and require an immediate $5k tier upgrade.",
        "ask": "Please reply 'Approved' by 2 PM today so DevOps can scale the servers before we face downtime.",
        "rationale": "Front-loads the 'why' and exactly what is needed, offering a frictionless 1-word approval path."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Server issues today",
        "opener": "Just wanted to give you a heads up that our traffic is going crazy today from the PR campaign.",
        "ask": "We probably need to bump up our AWS tier by $5k. Does that sound okay?",
        "rationale": "Conversational but lacks a clear urgency marker in the subject and asks a passive question."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "AWS bill",
        "opener": "The site is going to crash if we don't spend more money.",
        "ask": "Fix this please.",
        "rationale": "Alarmist without providing the exact cost, context, or professional phrasing."
      }
    ]
  },
  {
    "id": "bluf_04",
    "scenarioContext": "You need Legal to review and redline a Salesforce renewal contract before the current one expires on the 15th.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Review Needed: Salesforce Renewal Contract (Due by 12th)",
        "opener": "Attached is the annual Salesforce renewal contract requiring legal redlining.",
        "ask": "Please provide your redlines by the 12th so we can execute before the 15th expiration.",
        "rationale": "Clear subject line, immediately identifies the attachment, and gives a buffer deadline."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Salesforce Contract",
        "opener": "I just got the new contract from our Salesforce rep.",
        "ask": "Can someone on the legal team take a look before it expires next week?",
        "rationale": "Passes the buck without assigning a hard deadline, risking a lapse in service."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Contract attached",
        "opener": "Please see attached.",
        "ask": "Let me know if it's okay to sign.",
        "rationale": "Zero context. Legal receives dozens of these daily and will deprioritize it."
      }
    ]
  },
  {
    "id": "bluf_05",
    "scenarioContext": "You need your Director to approve travel expenses for the team offsite by Wednesday.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Needed: Team Offsite Travel Approval (Due Wed EOD)",
        "opener": "I have finalized the travel estimates for the Q2 team offsite at $4,200.",
        "ask": "Please approve via the attached Concur link by Wednesday EOD so we can book flights.",
        "rationale": "Uses a clear tag, states the total cost immediately, and provides a direct link to act."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Offsite planning",
        "opener": "The team is super excited about the offsite. I put together the flight and hotel costs.",
        "ask": "Can you approve it in Concur soon?",
        "rationale": "Buried ask, missing the total amount in the body, and 'soon' is not a deadline."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Flights",
        "opener": "Prices are going up for the offsite.",
        "ask": "I need you to approve it now.",
        "rationale": "Aggressive tone, missing context, and lacks the actual link to approve."
      }
    ]
  },
  {
    "id": "bluf_06",
    "scenarioContext": "You must inform the project sponsor that the mobile app launch is delayed by 2 weeks due to an App Store rejection.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Project Update: Mobile App Launch Delayed to Oct 15",
        "opener": "The mobile app launch is delayed by two weeks due to a sudden Apple App Store compliance rejection.",
        "ask": "No action is needed from you; engineering is deploying the required fix by Friday for resubmission.",
        "rationale": "Bad news is front-loaded in the subject, immediately explains why, and clearly states no action is required."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "App Store Status",
        "opener": "We have been working hard on getting the app through the Apple review process.",
        "ask": "Unfortunately they rejected it, so we have to push the launch back a couple of weeks to fix it.",
        "rationale": "Buries the bad news at the end of the paragraph and uses vague timelines ('couple of weeks')."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Bad news...",
        "opener": "Apple is being unreasonable again.",
        "ask": "We aren't launching on time. Will update you later.",
        "rationale": "Unprofessional tone, lacks a specific new date, and leaves the sponsor anxious."
      }
    ]
  },
  {
    "id": "bluf_07",
    "scenarioContext": "The new website redesign is facing heavy scope creep, and you need the client to formally pause adding new features.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Required: Feature Freeze Needed to Hit Launch Date",
        "opener": "To meet our agreed November 1st launch, we must implement a strict feature freeze immediately.",
        "ask": "Please confirm by EOD that the current scope is locked, and we will push all remaining ideas to a Phase 2.",
        "rationale": "Frames the restriction as a benefit to the client's timeline and forces a written confirmation."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Website design scope",
        "opener": "You guys have had some great ideas lately, but it's adding a lot to our plate.",
        "ask": "We should probably stop adding things if you still want it done by November.",
        "rationale": "Wishy-washy language ('probably', 'should') fails to establish a firm boundary."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Scope creep",
        "opener": "You keep adding features and we can't keep up.",
        "ask": "Stop sending new requests.",
        "rationale": "Antagonistic, unprofessional, and damages the client relationship."
      }
    ]
  },
  {
    "id": "bluf_08",
    "scenarioContext": "A critical supply chain delay means Q4 hardware won't arrive until January. You must brief the CEO.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Exec Brief: Q4 Hardware Delivery Delayed to Jan 10",
        "opener": "Our primary supplier faced a factory shutdown, delaying our Q4 hardware shipment to January 10th.",
        "ask": "Attached is the revised revenue forecast. I am available for a 15-minute sync this afternoon to discuss mitigation.",
        "rationale": "Executives need the bottom line instantly. Offers immediate data and a proactive meeting."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Supplier update",
        "opener": "I just got off the phone with our factory rep in Taiwan and there was a major issue.",
        "ask": "They can't ship until January, so we need to adjust our numbers.",
        "rationale": "Reads like a diary entry. Lacks revised data and puts the burden of next steps on the CEO."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Huge problem",
        "opener": "We aren't getting the hardware this year.",
        "ask": "What do you want us to do?",
        "rationale": "Panic-inducing subject line and complete lack of problem-solving initiative."
      }
    ]
  },
  {
    "id": "bluf_09",
    "scenarioContext": "Your lead engineer is out sick for a week, delaying a core feature. You need Product to adjust the roadmap.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Roadmap Adjustment: 'Dark Mode' Feature Delayed 1 Week",
        "opener": "Due to unexpected medical leave for our lead engineer, the 'Dark Mode' feature is delayed by one week.",
        "ask": "Please adjust the public roadmap to reflect a new launch date of the 22nd. No other features are impacted.",
        "rationale": "Clearly scopes the blast radius (only 1 feature impacted) and gives Product exact instructions."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Engineer out sick",
        "opener": "John is going to be out all week with the flu, so he can't work on Dark Mode.",
        "ask": "We will have to push it back a bit.",
        "rationale": "Shares unnecessary personal medical details and uses imprecise timeline adjustments ('a bit')."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Delays",
        "opener": "We are behind schedule.",
        "ask": "Update the roadmap.",
        "rationale": "Fails to specify *what* is behind schedule or by how much, rendering it useless."
      }
    ]
  },
  {
    "id": "bluf_10",
    "scenarioContext": "A vendor completely failed to deliver a critical video asset for tomorrow's ad campaign launch. You need to delay the launch.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Urgent: Ad Campaign Paused (Vendor Asset Failure)",
        "opener": "We must pause tomorrow's ad campaign launch because the vendor failed to deliver the finalized video asset.",
        "ask": "Please confirm all ad spend is frozen. I am escalating with the vendor and will provide an ETA by EOD.",
        "rationale": "Stops the bleeding (freezing ad spend) immediately and establishes ownership of the escalation."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Video vendor issue",
        "opener": "The video agency really dropped the ball and didn't send us the file today.",
        "ask": "Should we hold off on launching the ads tomorrow?",
        "rationale": "Asks a question instead of making a confident operational recommendation."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "No video",
        "opener": "Vendor screwed up.",
        "ask": "Campaign is dead.",
        "rationale": "Hyperbolic, unprofessional tone, and offers no path forward."
      }
    ]
  },
  {
    "id": "bluf_11",
    "scenarioContext": "Send the standard Monthly KPI report to the CEO, highlighting a 15% increase in user acquisition.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "FYI: October KPI Report (User Acq up 15%)",
        "opener": "Attached is the October KPI report. Topline metric: User acquisition grew by 15% month-over-month.",
        "ask": "Review at your convenience; no immediate action required.",
        "rationale": "Uses 'FYI' to denote low urgency, puts the headline in the subject, and explicitly states no action is needed."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Monthly Report",
        "opener": "Here is the report for October.",
        "ask": "We had a really good month with user acquisition. Let me know if you have questions.",
        "rationale": "Forces the CEO to open the attachment or read closely to find the actual percentage increase."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Stats",
        "opener": "See attached.",
        "ask": "Good numbers.",
        "rationale": "Too brief. Executives will not open an attachment without context."
      }
    ]
  },
  {
    "id": "bluf_12",
    "scenarioContext": "Provide a post-mortem to the CTO regarding yesterday's 45-minute database outage.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Post-Mortem: 45-Min Database Outage (Root Cause Identified)",
        "opener": "Yesterday's 45-minute outage was caused by a runaway memory leak in the billing microservice.",
        "ask": "A permanent patch is deployed. Attached is the full technical RCA for your review.",
        "rationale": "Reassures leadership that the crisis is over and the root cause is resolved, linking to deep data."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "About yesterday's outage",
        "opener": "We finally figured out what happened yesterday with the database going down.",
        "ask": "It was a memory leak. We fixed it. Report is attached.",
        "rationale": "A bit too informal for an engineering post-mortem and wastes words getting to the point."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "It broke",
        "opener": "We had a leak.",
        "ask": "All good now.",
        "rationale": "Unacceptable lack of professionalism and technical detail for a CTO."
      }
    ]
  },
  {
    "id": "bluf_13",
    "scenarioContext": "Inform the Board that a key competitor just pivoted to a freemium model, and you need to schedule a strategy sync.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Strategy Sync: Competitor Pivot to Freemium Model",
        "opener": "Competitor X just launched a freemium tier that directly undercuts our entry-level pricing.",
        "ask": "Please reply with your availability this Thursday for a 30-minute sync to review our counter-strategy.",
        "rationale": "Clearly states the threat and immediately pivots to actionable scheduling for a solution."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Competitor X update",
        "opener": "Did everyone see that Competitor X just went freemium?",
        "ask": "We should probably talk about this soon so we don't lose market share.",
        "rationale": "Uses a passive-aggressive rhetorical question and lacks a concrete scheduling ask."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "We are in trouble",
        "opener": "They dropped their prices to zero.",
        "ask": "Call a meeting.",
        "rationale": "Alarmist, unprofessional, and fails to take the lead on organizing the response."
      }
    ]
  },
  {
    "id": "bluf_14",
    "scenarioContext": "Update the VP of Sales that Q3 churn spiked by 4% due to the new pricing rollout.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Exec Update: Q3 Churn Spiked 4% (Pricing Rollout Impact)",
        "opener": "Q3 churn increased by 4%, directly correlated to customer friction from last month's pricing rollout.",
        "ask": "Attached is the cohort analysis. I recommend we temporarily pause price hikes for legacy accounts.",
        "rationale": "Delivers bad news cleanly, explains the 'why', and offers a proactive solution."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Q3 Churn Numbers",
        "opener": "The Q3 numbers are in, and churn is higher than we wanted.",
        "ask": "It looks like the pricing change upset people. We should look at the data attached.",
        "rationale": "Vague about the exact percentage and lacks a strong recommendation."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Churn",
        "opener": "Everyone is leaving because of the new prices.",
        "ask": "We need to revert.",
        "rationale": "Emotional, hyperbolic, and lacks data to back up the strong demand."
      }
    ]
  },
  {
    "id": "bluf_15",
    "scenarioContext": "Announce the successful launch of the new European data center to the entire company.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "FYI: European Data Center Successfully Launched",
        "opener": "I am thrilled to announce that our new EU data center is officially live and routing traffic.",
        "ask": "Huge thanks to the infrastructure team. No action is needed; enjoy the massive latency improvements!",
        "rationale": "Celebratory but maintains BLUF. Clearly tells the company no action is needed on their part."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Great news!",
        "opener": "We have been working on this for 6 months and it's finally done.",
        "ask": "The EU data center is live. Great job everyone.",
        "rationale": "Buries the actual announcement under generic filler text."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "It's alive",
        "opener": "Servers are up.",
        "ask": "Thanks.",
        "rationale": "Far too brief and lacks the professional polish required for an all-company broadcast."
      }
    ]
  },
  {
    "id": "bluf_16",
    "scenarioContext": "A top enterprise client is threatening to break their contract due to chronic slow support response times.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Escalation: Client X Threatening Churn (Support Delays)",
        "opener": "Client X is threatening to break their contract due to three consecutive missed SLAs in support.",
        "ask": "I need VP approval by 2 PM to offer them a dedicated account manager at no charge to save the account.",
        "rationale": "Clearly labels the escalation, identifies the risk, and asks for exact leverage to save the deal."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Client X is angry",
        "opener": "Client X is really mad about how slow our support has been.",
        "ask": "They might leave. Can we offer them something to stay?",
        "rationale": "Too vague. Fails to suggest a specific concession and lacks a timeline."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Help",
        "opener": "Client X is leaving.",
        "ask": "What do we do?",
        "rationale": "Panic-stricken. Dumps the problem entirely on leadership without proposing a solution."
      }
    ]
  },
  {
    "id": "bluf_17",
    "scenarioContext": "A VIP customer experienced localized data loss during a routine maintenance window.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Critical: VIP Client Data Loss Incident (Recovery in Progress)",
        "opener": "During last night's maintenance, VIP Client Y experienced a localized data deletion on their dashboard.",
        "ask": "Engineering expects full data restoration by 4 PM. I have drafted an apology email for your review attached.",
        "rationale": "Provides the status of the fix immediately and brings a drafted solution to the executive."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Client Y issue",
        "opener": "We had a bug last night and some of Client Y's data got deleted.",
        "ask": "Engineering is working on it. How should we tell them?",
        "rationale": "Passes the buck on client communication rather than drafting a proactive response."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Data gone",
        "opener": "We deleted Client Y's stuff.",
        "ask": "They are going to be mad.",
        "rationale": "Callous, unprofessional, and offers zero reassurance of recovery."
      }
    ]
  },
  {
    "id": "bluf_18",
    "scenarioContext": "A client is requesting a massive custom feature that your product team has explicitly refused to build.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Alignment Needed: Declining Client Z's Custom Feature Request",
        "opener": "Client Z requested a custom reporting dashboard, but Product has confirmed this is off-roadmap for 2026.",
        "ask": "I will inform the client tomorrow. Please let me know by EOD if you want to intervene before I send the rejection.",
        "rationale": "Informs leadership of a tough client conversation but gives them a clear window to override if necessary."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Client Z feature",
        "opener": "Product says we can't build the dashboard Client Z asked for.",
        "ask": "Should I just tell them no?",
        "rationale": "Asks for permission rather than stating intent. Shows a lack of account ownership."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "No to Client Z",
        "opener": "We aren't building it.",
        "ask": "You tell them.",
        "rationale": "Insubordinate and pushes account management duties onto someone else."
      }
    ]
  },
  {
    "id": "bluf_19",
    "scenarioContext": "A major enterprise client's invoice is 90 days past due, and you need Finance to formally lock their account.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Required: Lock Account for Client A (90 Days Past Due)",
        "opener": "Client A has ignored three payment warnings and is now 90 days past due on their $40k invoice.",
        "ask": "Please initiate the formal account lock protocol today. I have already notified their primary point of contact.",
        "rationale": "Gives Finance explicit instructions and confirms that the relationship management aspect is handled."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Client A payment",
        "opener": "Client A still hasn't paid us and it's been 3 months.",
        "ask": "I think we should probably lock their account soon.",
        "rationale": "Uses weak language ('I think', 'probably') for a severe financial protocol."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Lock them out",
        "opener": "They aren't paying.",
        "ask": "Shut it down.",
        "rationale": "Lacks the specific client name in the subject, the invoice amount, and professional decorum."
      }
    ]
  },
  {
    "id": "bluf_20",
    "scenarioContext": "You overcharged a client by $10,000 due to a billing error. You need to inform them and promise a refund.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Billing Error on Invoice #402: $10,000 Refund Initiated",
        "opener": "Due to a system glitch, your account was overcharged by $10,000 on Invoice #402.",
        "ask": "I sincerely apologize. We have already initiated a full refund to your account, which will process in 3-5 days.",
        "rationale": "Total transparency. The subject line owns the mistake and immediately provides the resolution."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Regarding your recent invoice",
        "opener": "We were looking at your account and noticed a mistake.",
        "ask": "You were overcharged, so we will send you a refund soon.",
        "rationale": "Vague subject line makes the client anxious, and lacks specific dollar amounts or timelines."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Oops",
        "opener": "We messed up your bill.",
        "ask": "Sending money back now.",
        "rationale": "Highly unprofessional tone for a severe financial error, damaging enterprise trust."
      }
    ]
  },
  {
    "id": "bluf_21",
    "scenarioContext": "You need the DevOps lead to prioritize scaling the infrastructure for a major marketing launch tomorrow.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Priority Request: Scale Infrastructure for Tomorrow's Launch",
        "opener": "Marketing is launching the new campaign tomorrow at 9 AM, and we expect a 3x traffic multiplier.",
        "ask": "Please confirm by EOD today that the servers are pre-scaled to handle this load.",
        "rationale": "Provides the exact multiplier needed and the strict deadline for confirmation."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Tomorrow's launch",
        "opener": "Hey, we have a big launch tomorrow and expect a lot of traffic.",
        "ask": "Can you make sure the servers don't crash?",
        "rationale": "Lacks specific data (3x multiplier) and puts the burden of guessing the scale on DevOps."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Servers",
        "opener": "Launch tomorrow.",
        "ask": "Be ready.",
        "rationale": "Cryptic and unhelpful. Fails to provide any actionable engineering metrics."
      }
    ]
  },
  {
    "id": "bluf_22",
    "scenarioContext": "You need Sales to stop promising a highly requested feature that Engineering has deemed impossible.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Needed: Cease Promising 'Feature X' on Sales Calls",
        "opener": "Engineering has permanently shelved 'Feature X' due to architectural limitations.",
        "ask": "Please instruct your AEs to stop promising this feature on calls immediately to prevent future churn.",
        "rationale": "Directly identifies the behavioral change needed and provides the hard boundary from engineering."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Feature X",
        "opener": "I noticed sales is still telling people we are building Feature X.",
        "ask": "We aren't building it anymore, so please tell them to stop.",
        "rationale": "A bit accusatory and lacks the firm, structural authority of the optimal choice."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Stop lying",
        "opener": "Sales keeps lying about features.",
        "ask": "Tell them to knock it off.",
        "rationale": "Hostile, unprofessional, and creates toxic cross-departmental friction."
      }
    ]
  },
  {
    "id": "bluf_23",
    "scenarioContext": "You need HR to fast-track an offer letter for a highly competitive engineering candidate by today.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Urgent: Fast-Track Offer Letter for Candidate Y (Due Today)",
        "opener": "Candidate Y has a competing offer expiring tomorrow, and we need to secure them.",
        "ask": "Please generate and send their finalized offer letter by 4 PM today. All approvals are in Greenhouse.",
        "rationale": "Explains the severe urgency, tells HR exactly what is needed, and assures them prerequisites are met."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Offer for Candidate Y",
        "opener": "We really want to hire Candidate Y, but they have another offer.",
        "ask": "Can we get their offer letter out today?",
        "rationale": "Polite, but lacks a strict hourly deadline, risking losing the candidate."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Hurry up",
        "opener": "We are going to lose the candidate.",
        "ask": "Send the offer now.",
        "rationale": "Rude, lacks candidate specifics, and fails to direct HR to the approval system."
      }
    ]
  },
  {
    "id": "bluf_24",
    "scenarioContext": "You need IT to grant a new contractor temporary admin access to a secure repository.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Access Request: Temporary Admin Repo Access for Contractor Z",
        "opener": "Contractor Z requires temporary admin access to the 'Core_Backend' repo to complete their audit.",
        "ask": "Please provision their access for 14 days. Manager approval is CC'd on this thread.",
        "rationale": "Provides exact system names, timelines for auto-revocation, and pre-clears the security approval."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Repo Access",
        "opener": "The new contractor needs to get into the backend repo.",
        "ask": "Can you give them admin rights for a bit?",
        "rationale": "Violates IT security norms by asking for open-ended admin rights without providing proof of manager approval."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Blocked",
        "opener": "Contractor is blocked.",
        "ask": "Fix their permissions.",
        "rationale": "Zero context. IT will instantly close this ticket for lack of information."
      }
    ]
  },
  {
    "id": "bluf_25",
    "scenarioContext": "You need Product to define the specific technical requirements for a feature before Engineering can sprint on it.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Blocker: Technical Specs Required for 'Feature W'",
        "opener": "Engineering is blocked from starting 'Feature W' because the Jira ticket lacks technical acceptance criteria.",
        "ask": "Please update the ticket with specific API payload requirements by Wednesday so we can include it in the sprint.",
        "rationale": "Clearly labels the blocker and asks for specific, technical deliverables by a set date."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Feature W ticket",
        "opener": "The Jira ticket for Feature W is pretty empty.",
        "ask": "Can you add more details so the devs know what to build?",
        "rationale": "'More details' is subjective and vague, likely resulting in another round of back-and-forth."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Bad ticket",
        "opener": "You didn't finish writing the ticket.",
        "ask": "Do your job.",
        "rationale": "Hostile and violates basic workplace professionalism."
      }
    ]
  },
  {
    "id": "bluf_26",
    "scenarioContext": "You are notifying the security team that a low-priority vulnerability was detected by a bug bounty hunter.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "FYI: Low-Priority XSS Bug Reported via Bounty Program",
        "opener": "A bounty hunter reported a low-severity XSS vulnerability on the legacy blog domain.",
        "ask": "No customer data is at risk. Please review the attached HackerOne report and slot a fix for next sprint.",
        "rationale": "Instantly defuses panic by classifying severity, stating data is safe, and suggesting a calm timeline."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Security bug found",
        "opener": "Someone submitted a bug report for an XSS issue.",
        "ask": "It doesn't look too bad, but someone should check it out when they have time.",
        "rationale": "Subject line is slightly alarmist for a low-priority issue, and the ask is too casual."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "We got hacked?",
        "opener": "A hacker sent us a message about a bug.",
        "ask": "Look into it.",
        "rationale": "Spreads unnecessary panic through poor word choice ('hacked', 'hacker')."
      }
    ]
  },
  {
    "id": "bluf_27",
    "scenarioContext": "You need the CFO to approve a change in vendor for office supplies to save 20% annually.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Approval: Vendor Switch to Save 20% on Office Supplies",
        "opener": "Switching our office supply vendor to 'Vendor B' will reduce our annual OPEX by 20% ($12k).",
        "ask": "Please approve the attached contract termination for our current vendor by Friday.",
        "rationale": "Execs love saving money. Puts the ROI directly in the subject line."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "New vendor",
        "opener": "I found a new vendor for office supplies that is a lot cheaper.",
        "ask": "Let me know if I have the green light to switch us over.",
        "rationale": "Fails to quantify the savings ('a lot cheaper') making it hard for Finance to evaluate."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Supplies",
        "opener": "Our current vendor is a rip off.",
        "ask": "I'm switching us.",
        "rationale": "Insubordinate and circumvents formal financial approval chains."
      }
    ]
  },
  {
    "id": "bluf_28",
    "scenarioContext": "You need to ask the Data Science team to pull a custom list of users who haven't logged in for 90 days.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Data Request: 90-Day Inactive User Cohort (Due Wed)",
        "opener": "Marketing needs a CSV export of all users who have not logged in for 90+ days for a re-engagement campaign.",
        "ask": "Please provide this pull by Wednesday EOD. Jira ticket with exact SQL parameters is linked below.",
        "rationale": "Provides the 'why' (re-engagement campaign), the format (CSV), and links to a formal ticket."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Need some data",
        "opener": "Can you guys pull a list of inactive users for us?",
        "ask": "We need it for a marketing thing this week.",
        "rationale": "Lacks specific constraints (90 days) and forces the data team to ask clarifying questions."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "CSV",
        "opener": "Give me the inactive users.",
        "ask": "ASAP.",
        "rationale": "Rude, demanding, and utterly lacking in necessary technical parameters."
      }
    ]
  },
  {
    "id": "bluf_29",
    "scenarioContext": "You are escalating a severe HR complaint regarding a manager's inappropriate behavior at a company event.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Confidential Escalation: HR Incident Report (Manager Name)",
        "opener": "I am formally escalating an incident of inappropriate conduct by [Manager] at last night's offsite.",
        "ask": "Attached is a secure document detailing the event. I request a private Zoom call with Employee Relations today.",
        "rationale": "Highly professional, maintains strict confidentiality, and immediately requests the proper procedural next step."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "What happened last night",
        "opener": "We need to talk about [Manager's] behavior at the party last night.",
        "ask": "It was really inappropriate. When can we talk?",
        "rationale": "A bit too informal for a serious HR escalation, lacking the phrase 'formal incident report'."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Fire [Manager]",
        "opener": "They acted crazy last night.",
        "ask": "Do something about it.",
        "rationale": "Unprofessional, emotional, and dictates disciplinary action rather than reporting facts."
      }
    ]
  },
  {
    "id": "bluf_30",
    "scenarioContext": "You are declining an invitation to a 2-hour brainstorming meeting because you have a conflicting deadline.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "RSVP Decline: Brainstorming Sync (Conflicting Deadline)",
        "opener": "I am unable to attend tomorrow's 2-hour brainstorm due to a hard deadline on the Q3 reporting.",
        "ask": "Please share the Miro board afterward, and I will add my notes asynchronously by Friday.",
        "rationale": "Polite decline, clear business reason, and offers a proactive asynchronous contribution."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Can't make it",
        "opener": "I'm too busy to come to the brainstorm tomorrow.",
        "ask": "Sorry!",
        "rationale": "A bit abrupt and fails to offer an alternative way to contribute to the project."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "No",
        "opener": "2 hours is too long.",
        "ask": "Not coming.",
        "rationale": "Combative and damages internal team relationships."
      }
    ]
  },
  {
    "id": "bluf_31",
    "scenarioContext": "A vendor's API changes are deprecating a critical integration. You need the CTO to allocate emergency engineering resources.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Critical: Vendor API Deprecation Requires Emergency Sprints",
        "opener": "Stripe is deprecating their V1 API in 30 days, which will break our checkout flow.",
        "ask": "I need approval to pull two senior engineers off the current sprint to rewrite the integration immediately.",
        "rationale": "States the existential threat (checkout breaking) and requests specific resources to solve it."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Stripe API update",
        "opener": "Stripe is changing their API soon and it's going to mess up our checkout.",
        "ask": "We should probably get some engineers to fix it.",
        "rationale": "Lacks the specific 30-day timeline and fails to make a hard resource request."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Checkout is doomed",
        "opener": "Stripe is ruining everything.",
        "ask": "Fix it.",
        "rationale": "Unprofessional, panicky, and offers zero constructive planning."
      }
    ]
  },
  {
    "id": "bluf_32",
    "scenarioContext": "The marketing team wants to change the company logo on the website, but you need Brand to approve it first.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Approval Needed: Temporary Logo Update for Pride Month",
        "opener": "Marketing wishes to update the website logo to the Pride variant for the month of June.",
        "ask": "Please review the attached mockups and provide Brand approval by Friday so Dev can deploy on the 1st.",
        "rationale": "Provides the 'what', the 'why' (Pride month), and the strict timeline for engineering."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Logo change",
        "opener": "We want to use the Pride logo on the site next month.",
        "ask": "Is that cool with you guys?",
        "rationale": "Too casual for a formal brand asset change and lacks a specific deadline."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Website update",
        "opener": "Changing the logo tomorrow.",
        "ask": "Let me know if there's a problem.",
        "rationale": "Circumvents authority by doing it first and asking for permission later."
      }
    ]
  },
  {
    "id": "bluf_33",
    "scenarioContext": "A key stakeholder missed the project kickoff meeting. You need to send them the recording and action items.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Items & Recording: Project Alpha Kickoff",
        "opener": "Attached is the recording from today's Project Alpha kickoff.",
        "ask": "Your specific action item is to approve the Phase 1 budget by Thursday. Let me know if you have questions.",
        "rationale": "Does not guilt them for missing the meeting, provides the resource, and explicitly isolates their specific task."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Missed you at kickoff",
        "opener": "Sorry you couldn't make it to the meeting today.",
        "ask": "Watch the recording when you can, there are some things you need to do.",
        "rationale": "Forces the stakeholder to watch an entire video just to figure out what their task is."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Meeting",
        "opener": "You missed the meeting.",
        "ask": "Watch the video.",
        "rationale": "Passive-aggressive and unhelpful."
      }
    ]
  },
  {
    "id": "bluf_34",
    "scenarioContext": "You need to formally reprimand a vendor for repeatedly ignoring security protocols when accessing your servers.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Compliance Warning: Repeated Security Protocol Violations",
        "opener": "Your team has bypassed our mandatory VPN gateway three times this week when accessing our servers.",
        "ask": "This is a formal warning. Future violations will result in immediate termination of the contract.",
        "rationale": "Sets a severe, uncompromising boundary regarding enterprise security and establishes a paper trail."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Server access",
        "opener": "Please remind your team to use the VPN when logging in.",
        "ask": "Security is getting mad about it.",
        "rationale": "Too soft. Fails to establish the severity of a security breach or the consequences."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "VPN",
        "opener": "Use the VPN.",
        "ask": "Or else.",
        "rationale": "Unprofessional and legally toothless as a formal warning."
      }
    ]
  },
  {
    "id": "bluf_35",
    "scenarioContext": "You are pitching a risky AI integration to the CEO that requires a $50k upfront investment.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Proposal: $50k AI Integration (Estimated $200k Annual ROI)",
        "opener": "I am proposing a $50k upfront investment in an AI support routing tool that will save $200k annually in headcount.",
        "ask": "Please review the attached 1-pager. I request 10 minutes at Friday's exec sync to pitch this fully.",
        "rationale": "Execs care about ROI. Highlighting the 4x return in the subject guarantees the email gets read."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "AI Tool idea",
        "opener": "I found a really cool AI tool that can help our support team.",
        "ask": "It costs $50k but I think it's worth it. Can we talk about it?",
        "rationale": "Focuses on the tool being 'cool' rather than the financial benefit, and buries the cost."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "AI",
        "opener": "We need to do AI.",
        "ask": "Give me $50k.",
        "rationale": "Absurdly brief and completely lacks business justification."
      }
    ]
  },
  {
    "id": "bluf_36",
    "scenarioContext": "Update the team that the company-wide holiday party has been canceled due to budget cuts.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Update: Company Holiday Party Canceled (Budget Reprioritization)",
        "opener": "Due to recent macro-economic shifts, leadership has made the difficult decision to cancel this year's holiday party.",
        "ask": "Instead, departments will receive a small budget for localized team lunches. Please reach out to your managers for details.",
        "rationale": "Rips the band-aid off respectfully in the subject, explains why, and offers a small consolation."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Holiday plans",
        "opener": "I know everyone was looking forward to the party.",
        "ask": "Unfortunately we had to cancel it because of the budget. Sorry!",
        "rationale": "Misleading subject line gives false hope, and the tone is slightly patronizing."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "No party",
        "opener": "We have no money.",
        "ask": "Party is off.",
        "rationale": "Brutal and damaging to company morale."
      }
    ]
  },
  {
    "id": "bluf_37",
    "scenarioContext": "A critical third-party API is experiencing a complete outage, taking down your core product. Notify internal stakeholders.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Severity 1 Outage: Core Product Down via Third-Party API",
        "opener": "Our core product is currently offline due to a complete outage at [Vendor Name].",
        "ask": "Engineering is actively monitoring. CS should use the attached macro to respond to client tickets until further notice.",
        "rationale": "Labels the severity, explains the external fault, and gives Customer Success an immediate action plan."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Site is down",
        "opener": "Just a heads up that the product isn't working right now because of our vendor.",
        "ask": "We will let you know when it's back up.",
        "rationale": "Fails to provide Customer Success with instructions on how to handle the incoming flood of tickets."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Down",
        "opener": "It's broken.",
        "ask": "Don't email engineering.",
        "rationale": "Unprofessional and leaves the rest of the company completely in the dark."
      }
    ]
  },
  {
    "id": "bluf_38",
    "scenarioContext": "Request an extension on a major compliance audit from the external auditing firm.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Extension Request: SOC2 Compliance Audit (Requesting +7 Days)",
        "opener": "Due to a sudden key personnel departure, we require a 7-day extension on our SOC2 evidence submission.",
        "ask": "Please confirm if we can adjust our final submission deadline to the 21st.",
        "rationale": "Formal, explains the specific internal reason without over-sharing, and requests an exact new date."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Audit timeline",
        "opener": "We are a bit behind on getting the audit files together.",
        "ask": "Can we have a few extra days?",
        "rationale": "Vague ('a few extra days') and lacks a compelling professional justification for the delay."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Late",
        "opener": "We aren't going to make the deadline.",
        "ask": "Deal with it.",
        "rationale": "Rude and highly likely to trigger an immediate failure of the compliance audit."
      }
    ]
  },
  {
    "id": "bluf_39",
    "scenarioContext": "You need the design team to finalize 3 banner ads by tomorrow morning so you can launch the campaign.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Needed: Finalize 3 Banner Ads (Due 9 AM Tomorrow)",
        "opener": "We are ready to launch the Q4 campaign, but are blocked waiting on the final 3 banner ads.",
        "ask": "Please upload the final PNGs to the shared drive by 9 AM tomorrow so I can traffic them in the ad platform.",
        "rationale": "Directly states the blocker, the exact format needed (PNGs), where to put them, and the hard deadline."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Banner ads",
        "opener": "Hey, I need those banner ads soon.",
        "ask": "Can you finish them up so I can launch the campaign?",
        "rationale": "Missing the exact time deadline, which means Design might deliver them at 5 PM instead of 9 AM."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Where are the ads?",
        "opener": "You are holding up the launch.",
        "ask": "Send now.",
        "rationale": "Hostile and lacks specific delivery instructions."
      }
    ]
  },
  {
    "id": "bluf_40",
    "scenarioContext": "Notify the internal legal team that a former employee is threatening a wrongful termination lawsuit on LinkedIn.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Urgent Escalation: Former Employee Threatening Litigation Publicly",
        "opener": "Former employee [Name] is currently posting threats of a wrongful termination lawsuit on LinkedIn.",
        "ask": "Attached are screenshots. Please advise on immediate PR mitigation and legal cease-and-desist procedures.",
        "rationale": "Treats public legal threats with extreme urgency, secures evidence (screenshots), and demands immediate legal counsel."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Did you see LinkedIn?",
        "opener": "[Name] is posting some really bad stuff about us on LinkedIn.",
        "ask": "Should we reply to them or ignore it?",
        "rationale": "Downplays the severity of a legal threat and considers engaging publicly, which is a massive risk."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Crazy ex-employee",
        "opener": "Look at what they are posting.",
        "ask": "Make them stop.",
        "rationale": "Unprofessional terminology and lacks any helpful evidence."
      }
    ]
  },
  {
    "id": "bluf_41",
    "scenarioContext": "A new enterprise feature failed Quality Assurance testing and must be pulled from tomorrow's release.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Release Update: Pulling 'Feature Y' from Tomorrow's Deploy",
        "opener": "QA identified a critical data-corruption bug in 'Feature Y' during final regression testing.",
        "ask": "DevOps, please pull this feature from tomorrow's release branch. Sales, please notify beta clients of the delay.",
        "rationale": "Assigns explicit, cross-functional commands to mitigate the failure immediately."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Feature Y failed",
        "opener": "Feature Y didn't pass QA so we can't release it tomorrow.",
        "ask": "We will try again next week.",
        "rationale": "Fails to instruct DevOps to actively pull the branch, risking a broken build."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Buggy",
        "opener": "Feature Y is broken.",
        "ask": "Don't launch it.",
        "rationale": "Lacks professional structure and clear instructions."
      }
    ]
  },
  {
    "id": "bluf_42",
    "scenarioContext": "You need to ask the CEO to sign a formal press release quote before 5 PM so the PR firm can distribute it.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Action Needed: Approve CEO Quote for Press Release (Due 5 PM)",
        "opener": "The PR firm is ready to distribute the Series B announcement tomorrow morning.",
        "ask": "Please review and approve your drafted quote (highlighted in the attachment) by 5 PM today.",
        "rationale": "Isolates exactly what the CEO needs to read (the highlighted quote) to save them time."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Press Release approval",
        "opener": "Can you read the attached press release?",
        "ask": "We need to make sure you are okay with the quote we wrote for you before 5 PM.",
        "rationale": "Forces the CEO to read the entire document to find their quote."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Quote",
        "opener": "Look at the quote.",
        "ask": "Approve it.",
        "rationale": "Far too abrasive for executive communication."
      }
    ]
  },
  {
    "id": "bluf_43",
    "scenarioContext": "An enterprise client requested a 30% discount on their renewal. You need to tell them no, but offer 10%.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Regarding Your Contract Renewal (Pricing Options)",
        "opener": "While we cannot accommodate the requested 30% discount, we value your partnership immensely.",
        "ask": "I have secured executive approval for a 10% discount on a 2-year term. Please let me know if I can draft the paperwork.",
        "rationale": "Firmly delivers the 'no' but immediately cushions it with a realistic, pre-approved counter-offer."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Discount request",
        "opener": "I asked my boss about the 30% discount and they said no.",
        "ask": "The best we can do is 10%. Let me know what you think.",
        "rationale": "Throws the boss under the bus and sounds weak rather than presenting a unified corporate stance."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "No",
        "opener": "We aren't doing 30%.",
        "ask": "Take 10% or leave it.",
        "rationale": "Aggressive and guaranteed to cause the client to churn."
      }
    ]
  },
  {
    "id": "bluf_44",
    "scenarioContext": "You need to formally document that a cross-functional peer missed a vital deadline, delaying your team.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Project Status: Dependency Delay Impacting Phase 2",
        "opener": "As we did not receive the finalized API documentation yesterday, our engineering team is currently blocked.",
        "ask": "Please provide an updated ETA for the docs today so I can accurately adjust our Phase 2 delivery schedule.",
        "rationale": "Objective and factual. Focuses on the impact to the business rather than attacking the person."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Missed deadline",
        "opener": "You missed the deadline for the API docs yesterday.",
        "ask": "When are you going to send them so we can start working?",
        "rationale": "Slightly accusatory tone that will likely trigger a defensive response rather than collaboration."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Where are they?",
        "opener": "You are making my team look bad.",
        "ask": "Send the docs.",
        "rationale": "Highly toxic, emotional, and counterproductive to enterprise workflows."
      }
    ]
  },
  {
    "id": "bluf_45",
    "scenarioContext": "Notify your team that Friday afternoons are now strictly 'No Meeting' blocks for deep work.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Process Update: Instituting 'No Meeting Fridays' (Afternoons)",
        "opener": "To increase deep-work time and combat burnout, all Friday afternoons (1 PM - 5 PM) are now strict 'No Meeting' blocks.",
        "ask": "Please decline any internal invites during this time. Exceptions apply only for critical client escalations.",
        "rationale": "Clearly sets the new rule, explains the benefit, and handles the edge-case exceptions."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Friday meetings",
        "opener": "I think we all have too many meetings on Fridays.",
        "ask": "Let's try not to schedule anything on Friday afternoons from now on.",
        "rationale": "'Let's try' is not a policy. It leaves the rule ambiguous and unenforceable."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Fridays",
        "opener": "Stop booking meetings on Fridays.",
        "ask": "I'm sick of it.",
        "rationale": "Dictatorial, angry, and lacks any positive framing for the policy."
      }
    ]
  },
  {
    "id": "bluf_46",
    "scenarioContext": "You are formally warning a subordinate about excessive unscheduled absences.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Formal Notice: Unscheduled Absences and Attendance Policy",
        "opener": "This email serves as a formal written warning regarding your 4 unscheduled absences this month.",
        "ask": "Please review the attached employee handbook. Any further unscheduled absences may result in formal disciplinary action.",
        "rationale": "Maintains strict HR compliance, states the facts neutrally, and clearly outlines consequences."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Your attendance",
        "opener": "You have been missing a lot of work lately without telling anyone.",
        "ask": "You need to stop doing this or you will get in trouble with HR.",
        "rationale": "Too casual for a legally binding HR document and uses vague terms like 'a lot'."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Missing work",
        "opener": "Why do you keep missing work?",
        "ask": "I'm going to fire you if you do it again.",
        "rationale": "Violates corporate HR guidelines and creates immediate legal liability for the company."
      }
    ]
  },
  {
    "id": "bluf_47",
    "scenarioContext": "Request a mid-year budget adjustment from the board because inflation wiped out your travel stipend.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Board Approval: $20k Mid-Year Budget Adjustment (Travel Inflation)",
        "opener": "Due to a 30% macroeconomic spike in airfare, our annual travel budget is entirely depleted as of Q2.",
        "ask": "I request a $20k adjustment to fund critical Q3/Q4 client onsite visits. Full ROI analysis is attached.",
        "rationale": "Blames external factors logically, quantifies the exact need, and links it directly to revenue (client visits)."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Travel budget is gone",
        "opener": "Flights have been really expensive this year so we ran out of money.",
        "ask": "Can we get another $20k so the sales team can keep travelling?",
        "rationale": "Lacks executive polish and fails to justify the ROI of the extra spend."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Money",
        "opener": "We need more money to fly.",
        "ask": "Approve $20k.",
        "rationale": "Demanding and completely lacks justification for the board."
      }
    ]
  },
  {
    "id": "bluf_48",
    "scenarioContext": "You are transitioning an account to a new Account Manager because you are taking a new internal role.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Account Transition: Introducing Your New Account Manager",
        "opener": "I am transitioning to a new role internally, and [Name] will be taking over as your dedicated Account Manager.",
        "ask": "I have CC'd [Name] here. Please reply with a time next week for a brief 15-minute introductory handover call.",
        "rationale": "Clear subject, immediate introduction, and a proactive call-to-action to ensure continuity."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "I am leaving",
        "opener": "I just got a promotion so I won't be your account manager anymore.",
        "ask": "[Name] is going to take over. You can reach out to them if you need anything.",
        "rationale": "Makes it about the sender's promotion rather than the client's needs, and lacks a formal handover."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Bye",
        "opener": "I'm off this account.",
        "ask": "Talk to [Name].",
        "rationale": "Extremely rude and guarantees a poor client experience during the transition."
      }
    ]
  },
  {
    "id": "bluf_49",
    "scenarioContext": "Notify the internal team that the office will be closed tomorrow due to severe weather.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Urgent: Office Closed Tomorrow (Severe Weather) - WFH Mandated",
        "opener": "Due to the impending blizzard, the physical office will be strictly closed tomorrow for everyone's safety.",
        "ask": "All employees are expected to work from home. Please take your laptops home tonight.",
        "rationale": "Subject line contains the entire message. The body provides the logistical instruction (take laptops home)."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Weather tomorrow",
        "opener": "Looks like it's going to snow a lot tomorrow.",
        "ask": "We are going to close the office, so just work from home.",
        "rationale": "Lacks the urgency tag and fails to remind them to take their equipment home today."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Snow",
        "opener": "Don't come in tomorrow.",
        "ask": "Stay home.",
        "rationale": "Unprofessional and leaves ambiguity about whether they are expected to work remotely."
      }
    ]
  },
  {
    "id": "bluf_50",
    "scenarioContext": "You are rejecting a vendor's proposal because their pricing is triple your current budget.",
    "options": [
      {
        "type": "Optimal",
        "score": 100,
        "subject": "Regarding Your Proposal: Declining Due to Budget Constraints",
        "opener": "Thank you for the detailed proposal. Unfortunately, we must pass at this time as the pricing exceeds our Q3 budget.",
        "ask": "We will keep your information on file and reach out if our procurement parameters change next year.",
        "rationale": "Polite, firm, and provides a clear business reason without opening the door for annoying follow-ups."
      },
      {
        "type": "Suboptimal",
        "score": 50,
        "subject": "Your proposal",
        "opener": "We looked at your proposal and it's just way too expensive for us.",
        "ask": "We can't do business right now.",
        "rationale": "'Way too expensive' is slightly unprofessional and adversarial phrasing."
      },
      {
        "type": "Poor",
        "score": 0,
        "subject": "Too much money",
        "opener": "You guys charge too much.",
        "ask": "Don't email us again.",
        "rationale": "Hostile and burns an industry bridge unnecessarily."
      }
    ]
  }
];